import { useEffect, useState } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { X, CheckCircle2, AlertCircle, Package, RefreshCw } from 'lucide-react';

export default function MaterialCheckDialog({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    const r = await authFetch(`/api/order-material/${orderId}`);
    if (!r.ok) { setErr('Fehler beim Laden'); setLoading(false); return; }
    setData(await r.json());
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="material-check-dialog"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-[#9ec043]" />
            <h2 className="text-sm font-semibold text-[#32373c]">Materialcheck</h2>
            {data?.summary && (
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  data.summary.allOk
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-orange-50 text-orange-700'
                }`}
                data-testid="material-check-summary"
              >
                {data.summary.allOk
                  ? 'Alle Komponenten verfügbar'
                  : `${data.summary.totalComponents - data.summary.missingComponents} von ${data.summary.totalComponents} verfügbar`}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <button
              onClick={load}
              className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded"
              title="Neu prüfen"
              data-testid="btn-material-reload"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading && <div className="text-sm text-gray-400">Lade Lagerbestand…</div>}
          {err && <div className="text-sm text-red-500">{err}</div>}
          {data && data.positions.length === 0 && (
            <div className="text-sm text-gray-400">Keine Positionen im Auftrag.</div>
          )}
          {data && data.positions.map((pos, i) => (
            <div key={i} className="mb-5 last:mb-0">
              <div className="flex items-start gap-2 mb-2">
                {pos.type === 'system' ? (
                  <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-[#9ec043]/10 text-[#9ec043] rounded font-semibold">System</span>
                ) : (
                  <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 rounded font-semibold">Artikel</span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#32373c]">
                    {pos.systemName || pos.description?.split('\n')[0] || `Position ${i + 1}`}
                  </div>
                  <div className="text-[10px] text-gray-400">Menge: {pos.quantity}</div>
                </div>
              </div>

              <table className="w-full text-xs ml-7">
                <thead>
                  <tr className="text-left text-[10px] text-gray-400 border-b border-gray-100">
                    <th className="py-1 font-normal">Hersteller / Modell</th>
                    <th className="py-1 font-normal text-right">Benötigt</th>
                    <th className="py-1 font-normal text-right">Bestand</th>
                    <th className="py-1 font-normal text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pos.components.map((c, j) => (
                    <tr key={j} className="border-b border-gray-50 last:border-b-0" data-testid={`material-row-${i}-${j}`}>
                      <td className="py-1.5">
                        <div className="text-[#32373c]">{c.modell || '—'}</div>
                        <div className="text-[10px] text-gray-400">{c.hersteller || ''}</div>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{c.needed}</td>
                      <td className={`py-1.5 text-right tabular-nums ${c.stock < 0 ? 'text-red-600' : ''}`}>
                        {c.inLager ? c.stock : '—'}
                      </td>
                      <td className="py-1.5 text-right">
                        {!c.inLager ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-orange-600">
                            <AlertCircle className="w-3 h-3" /> nicht im Lager
                          </span>
                        ) : c.ok ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" /> verfügbar
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] text-red-600 font-medium">
                            <AlertCircle className="w-3 h-3" /> {c.missing} fehlen
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
